import Constants from "../constants/Constants";
import {
  convertBase64StringToArrayBuffer,
  convertArrayBufferToBase64String,
  logApiResponse,
} from "../util/Utility";
import {
  aeadDecryptWithAssociatedData,
  aeadEncryptWithAssociatedData,
} from "./AuthenticatedEncryption";
import {
  HKDF_INFO_ECDH_AES,
  HKDF_INFO_XDH_AES,
  hkdfSha256Raw,
} from "./KeyMaterial";
import { isCurve25519, getWebCryptoSubtle, normalizeEccCurve, requireSigningCurve } from "./CurveUtil";

const CURVE_CONFIG = {
  "P-384": {
    keyAgreement: { name: "ECDH", namedCurve: "P-384" },
    signing: { name: "ECDSA", namedCurve: "P-384" },
    deriveBitsLength: 384,
  },
  CURVE_25519: {
    keyAgreement: { name: "X25519" },
    signing: { name: "Ed25519" },
    deriveBitsLength: 256,
  },
};

function getEncryptionCurveConfig(encryptionCurve) {
  // normalizeEccCurve fails closed on absent/unknown input. Checking isCurve25519 alone would
  // treat every non-25519 value — including undefined — as P-384, silently importing an Ed25519
  // key as ECDSA and failing much later with an opaque WebCrypto error.
  normalizeEccCurve(encryptionCurve);
  if (isCurve25519(encryptionCurve)) {
    return CURVE_CONFIG["CURVE_25519"];
  }
  return CURVE_CONFIG["P-384"];
}

function getSigningCurveConfig(signingCurve) {
  requireSigningCurve(signingCurve);
  if (isCurve25519(signingCurve)) {
    return CURVE_CONFIG["CURVE_25519"];
  }
  return CURVE_CONFIG["P-384"];
}

/** @deprecated Use getEncryptionCurveConfig or getSigningCurveConfig */
function getCurveConfig(eccCurve) {
  return getEncryptionCurveConfig(eccCurve);
}

export { normalizeEccCurve, isCurve25519 };

/**
 * Generates a new ECC key pair for key agreement (encryption).
 *
 * @param {String} encryptionCurve The encryption curve from storage/API.
 * @returns {CryptoKeyPair} the generated key pair.
 */
export const generateEccKeyPair = async (encryptionCurve) => {
  const config = getEncryptionCurveConfig(encryptionCurve);
  const usages = isCurve25519(encryptionCurve)
    ? ["deriveBits"]
    : ["deriveKey", "deriveBits"];
  return await getWebCryptoSubtle().generateKey(
    config.keyAgreement,
    true,
    usages
  );
};

/**
 * Generates a new ECC key pair for digital signatures.
 *
 * @param {String} signingCurve The signing curve from storage/API.
 * @returns {CryptoKeyPair} the generated key pair.
 */
export const generateEccSigningKeyPair = async (signingCurve) => {
  const config = getSigningCurveConfig(signingCurve);
  return await getWebCryptoSubtle().generateKey(
    config.signing,
    true,
    ["sign", "verify"]
  );
};

/**
 * Imports a PEM-encoded signing public key as a CryptoKey.
 *
 * @param {String} pemKey The input key in PEM format.
 * @param {String} signingCurve The signing curve from storage/API.
 * @returns {CryptoKey} the PEM key as a CryptoKey object.
 */
export const loadEccSigningPublicKey = async (pemKey, signingCurve) => {
  // Curve first: an absent curve is a configuration fault and must surface as itself rather than
  // as whatever the key material happens to look like.
  const config = getSigningCurveConfig(signingCurve);
  // .trim() like every sibling loader below. Without it this was the one PEM entry point that
  // passed its argument through untouched, so a null key reached indexOf inside
  // _extractRawKeyMaterial and produced a TypeError naming neither the key nor the caller.
  const rawKey = _extractRawKeyMaterial(pemKey?.trim(), Constants.KEY_TYPE.PUBLIC);
  return await getWebCryptoSubtle().importKey(
    "spki",
    rawKey,
    config.signing,
    true,
    ["verify"]
  );
};

/**
 * Imports a PEM-encoded signing private key as a CryptoKey.
 *
 * @param {String} pemKey The input key in PEM format.
 * @param {String} signingCurve The signing curve from storage/API.
 * @returns {CryptoKey} the PEM key as a CryptoKey object.
 */
export const loadEccSigningPrivateKey = async (pemKey, signingCurve) => {
  const config = getSigningCurveConfig(signingCurve);
  const rawKey = _extractRawKeyMaterial(
    pemKey?.trim(),
    Constants.KEY_TYPE.PRIVATE
  );
  return await getWebCryptoSubtle().importKey(
    "pkcs8",
    rawKey,
    config.signing,
    true,
    ["sign"]
  );
};

/**
 * Encrypts the given text with the ECC publicKey using ECIES-like scheme.
 *
 * @param {String} plaintext The text that is to be encrypted.
 * @param {String} publicKey The public ECC key with which the plaintext is to be encrypted.
 * @param {String} associatedData The associated data to be used for encryption.
 * @param {String} encryptionCurve The encryption curve from storage/API.
 * @returns {Object} An object with encryptedtext and KEK.
 */
export const eccEncryptWithRandom = async (
  plaintext,
  publicKey,
  associatedData,
  encryptionCurve
) => {
  const eccPublicCryptoKey = await _loadEccPublicKey(publicKey.trim(), encryptionCurve);

  const randomKeyPair = await generateEccKeyPair(encryptionCurve);

  const sharedSecret = await _generateSharedSecret(
    eccPublicCryptoKey,
    randomKeyPair.privateKey,
    encryptionCurve
  );

  if (sharedSecret) {
    const encryptedPlainString = await aeadEncryptWithAssociatedData(
      plaintext,
      associatedData,
      sharedSecret
    );

    return {
      encryptedText: encryptedPlainString,
      keyEncryptionKey: await exportEccPublicKey(randomKeyPair.publicKey),
    };
  }

  throw new Error("Failed to derive shared secret for ECC encryption");
};

/**
 * Decrypts the given ciphertext with the provided privateKey.
 *
 * @param {String} encryptedText The ciphertext that is to be decrypted.
 * @param {String} privateKey The ECC privateKey with which the sharedSecret will be made.
 * @param {String} keyEncryptionKey The KEK is the publicKey with which the sharedSecret will be made.
 * @param {String} associatedData The associated data to be used for decryption.
 * @param {String} encryptionCurve The encryption curve from storage/API.
 * @returns {String} The decrypted text.
 */
export const eccDecryptWithRandom = async (
  encryptedText,
  privateKey,
  keyEncryptionKey,
  associatedData,
  encryptionCurve
) => {
  const eccPublicCryptoKey = await _loadEccPublicKey(keyEncryptionKey.trim(), encryptionCurve);
  const eccPrivateCryptoKey = await _loadEccPrivateKey(privateKey.trim(), encryptionCurve);

  const sharedSecret = await _generateSharedSecret(
    eccPublicCryptoKey,
    eccPrivateCryptoKey,
    encryptionCurve
  );

  if (sharedSecret) {
    return await aeadDecryptWithAssociatedData(
      encryptedText,
      associatedData,
      sharedSecret
    );
  }

  throw new Error("Failed to derive shared secret for ECC decryption");
};

/**
 * This function exports the key material of a ECC Private CryptoKey in the PEM format.
 *
 * @param {CryptoKey} cryptoKey The private ECC key object whose key material is to exported.
 * @returns {String} The exported private key in PEM format.
 */
export const exportEccPrivateKey = async (cryptoKey) => {
  const result = await getWebCryptoSubtle().exportKey("pkcs8", cryptoKey);
  const exportedAsBase64 = convertArrayBufferToBase64String(result);
  return `-----BEGIN PRIVATE KEY-----\n${exportedAsBase64}\n-----END PRIVATE KEY-----`;
};

/**
 * This function exports the key material of a ECC Public CryptoKey in the PEM format.
 *
 * @param {CryptoKey} cryptoKey The public ECC key object whose key material is to exported.
 * @returns {String} The exported public key in PEM format.
 */
export const exportEccPublicKey = async (cryptoKey) => {
  const result = await getWebCryptoSubtle().exportKey("spki", cryptoKey);
  const exportedAsBase64 = convertArrayBufferToBase64String(result);
  return `-----BEGIN PUBLIC KEY-----\n${exportedAsBase64}\n-----END PUBLIC KEY-----`;
};

export const normalizePem = (pem) => pem.replace(/\r\n/g, "\n").trim();

export const derivePublicKeyPemFromPrivateKeyPem = async (
  privateKeyPem,
  encryptionCurve,
) => {
  const config = getEncryptionCurveConfig(encryptionCurve);
  const privateCryptoKey = await _loadEccPrivateKey(privateKeyPem, encryptionCurve);
  const jwk = await getWebCryptoSubtle().exportKey("jwk", privateCryptoKey);
  delete jwk.d;
  jwk.key_ops = [];
  const publicCryptoKey = await getWebCryptoSubtle().importKey(
    "jwk",
    jwk,
    config.keyAgreement,
    true,
    [],
  );
  return exportEccPublicKey(publicCryptoKey);
};

export const publicKeysMatch = async (
  privateKeyPem,
  candidatePublicKeyPem,
  encryptionCurve,
) => {
  const derivedPublicKeyPem = await derivePublicKeyPemFromPrivateKeyPem(
    privateKeyPem,
    encryptionCurve,
  );
  return (
    normalizePem(derivedPublicKeyPem) === normalizePem(candidatePublicKeyPem)
  );
};

// Utility functions.

/**
 * Loads an ECC public key (key agreement) from PEM format.
 *
 * @param {String} pemKey The public key to be loaded, in PEM format.
 * @param {String} encryptionCurve The encryption curve from storage/API.
 * @returns {CryptoKey} The imported PEM public key.
 */
export async function _loadEccPublicKey(pemKey, encryptionCurve) {
  const rawKey = _extractRawKeyMaterial(
    pemKey.trim(),
    Constants.KEY_TYPE.PUBLIC
  );
  const config = getEncryptionCurveConfig(encryptionCurve);
  return await getWebCryptoSubtle().importKey(
    "spki",
    rawKey,
    config.keyAgreement,
    true,
    []
  );
}

/**
 * Loads an ECC private key (key agreement) from PEM format.
 *
 * @param {String} pemKey The private key to be loaded, in PEM format.
 * @param {String} encryptionCurve The encryption curve from storage/API.
 * @returns {CryptoKey} The imported PEM private key.
 */
export async function _loadEccPrivateKey(pemKey, encryptionCurve) {
  const rawKey = _extractRawKeyMaterial(
    pemKey.trim(),
    Constants.KEY_TYPE.PRIVATE
  );
  const config = getEncryptionCurveConfig(encryptionCurve);
  const usages = isCurve25519(encryptionCurve)
    ? ["deriveBits"]
    : ["deriveKey", "deriveBits"];

  return await getWebCryptoSubtle().importKey(
    "pkcs8",
    rawKey,
    config.keyAgreement,
    true,
    usages
  );
}

function getHkdfInfoForCurve(encryptionCurve) {
  // Must match the Java side: Util.HKDF_INFO_XDH_AES for X25519, HKDF_INFO_ECDH_AES for P-384.
  if (isCurve25519(encryptionCurve)) {
    return HKDF_INFO_XDH_AES;
  }
  return HKDF_INFO_ECDH_AES;
}

/**
 * ECDH / X25519 shared secret -> HKDF-SHA256 -> 32 raw AES key bytes.
 * Matches Java ECCEncryption.generateSharedSecret. Never Base64-truncates the agreement output.
 *
 * @param {CryptoKey} publicCryptoKey The publicKey used to create a sharedSecret.
 * @param {CryptoKey} privateCryptoKey The privateKey used to create a sharedSecret.
 * @param {String} encryptionCurve The encryption curve from storage/API.
 * @returns {Promise<Uint8Array>} 32-byte AES key
 */
export async function _generateSharedSecret(publicCryptoKey, privateCryptoKey, encryptionCurve) {
  const config = getEncryptionCurveConfig(encryptionCurve);
  const algorithmName = config.keyAgreement.name;
  const bitsLength = config.deriveBitsLength;

  const rawSharedSecret = await getWebCryptoSubtle().deriveBits(
    {
      name: algorithmName,
      public: publicCryptoKey,
    },
    privateCryptoKey,
    bitsLength
  );

  return await hkdfSha256Raw(
    new Uint8Array(rawSharedSecret),
    null,
    getHkdfInfoForCurve(encryptionCurve),
    32
  );
}

/**
 * This method removes the outer covers of a PEM key and return the raw key material.
 *
 * @param {String} pem The key whose raw material is to be extracted.
 * @param {String} type The type of the key of the PEM string.
 * @returns {ArrayBuffer} The extracted raw key material as an ArrayBuffer.
 */
function _extractRawKeyMaterial(pem, type) {
  const pemHeader = `-----BEGIN ${type} KEY-----`;
  const pemFooter = `-----END ${type} KEY-----`;

  // A missing key is a caller bug worth naming. Reaching indexOf with null threw
  // "Cannot read properties of null (reading 'indexOf')", which says nothing about what was
  // absent and reads to a user as a crash rather than as a missing key.
  if (typeof pem !== "string" || !pem) {
    throw new Error(`A ${type.toLowerCase()} key is required.`);
  }

  const headerIndex = pem.indexOf(pemHeader);
  const endingIndex = pem.indexOf(pemFooter);

  // Reject anything that is not this kind of PEM, instead of decoding whatever it happens to be.
  //
  // A missing header made indexOf return -1, so startingIndex became headerLength - 1 and
  // endingIndex -1; substring swaps reversed arguments, so the result was silently the first ~26
  // characters of the input. Handing that to atob failed with "The string to be decoded is not
  // correctly encoded" - three layers below the caller, naming neither the value nor what was
  // wrong with it. That is how an encrypted group key passed off as a signing key was diagnosed.
  if (headerIndex === -1 || endingIndex === -1 || endingIndex <= headerIndex) {
    throw new Error(
      `Expected a PEM-encoded ${type.toLowerCase()} key, got a value with no ${pemHeader} block.`,
    );
  }

  const startingIndex = headerIndex + pemHeader.length;

  const pemContents = pem.substring(startingIndex, endingIndex);
  var return_object = convertBase64StringToArrayBuffer(pemContents.trim());
  return return_object;
}
